import { useSuspenseQuery } from "@tanstack/react-query"
import { trpc } from "app/trpc"
import styled from "@emotion/styled"
import { FaTimes } from "@react-icons/all-files/fa/FaTimes"
import { Value } from "@stringtale/react"
import { useCurrentGroup } from "app/core/hooks/useCurrentGroup"
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  Suspense,
  useEffect,
  useState,
} from "react"
import { ADD_PLAYLIST_BLUE_COLOR, MODAL_Z_INDEX } from "theme/consts"
import { PX14 } from "theme/sizes"
import CheckboxBase from "ui/Checkbox"
import Group from "ui/Group"
import { Overlay as OverlayBase } from "ui/Modal"
import RoundedButton from "ui/RoundedButton"
import { Separator } from "ui/Separator"
import Stack from "ui/Stack"
import Text from "ui/Text"
import toggleArray from "utils/global/toggleArray"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerIconClose,
  DrawerOverlay,
  DrawerPortal,
} from "../../core/components/Drawer"
import AddLessonToListModalContent from "./AddLessonToListModalContent"
import { Dialog } from "radix-ui"

import { CheckedState } from "~/types"
import { AddSubmittableToPlaylistWarning } from "./AddSubmittableToPlaylistWarning"

const Checkbox = styled(CheckboxBase)`
  cursor: pointer;
`

const Overlay = styled(OverlayBase)`
  z-index: ${MODAL_Z_INDEX} + 1;
`

export type AddToPlaylistLesson = {
  id: number
  displayTitle: string | null
}

function DrawerContent_({
  isDrawerOpen,
  setIsDrawerOpen,
  userGroups,
  activeGroup,
  currentGrades,
  setGrades,
  deselectBoxState,
  setDeselectBoxState,
}: {
  isDrawerOpen: boolean
  setIsDrawerOpen: (open: boolean) => void
  userGroups: any
  activeGroup: any
  currentGrades: number[]
  setGrades: (grades: any) => void
  deselectBoxState: CheckedState
  setDeselectBoxState: (state: CheckedState) => void
}) {
  return (
    <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
      {isDrawerOpen && (
        <DrawerPortal>
          <DrawerOverlay />
          <DrawerContent>
            <DrawerHeader>
              <DrawerIconClose>
                <FaTimes size={20} />
              </DrawerIconClose>
            </DrawerHeader>
            <DrawerDescription>
              <Stack gap="12px">
                <Text bold>
                  <Value name="apps.web.src.studio.components.studiothemegroup.toon_groepen">
                    Toon groepen
                  </Value>
                </Text>
                <Separator />
                <Stack gap="18px">
                  <Group gap="16px" align="center">
                    <Checkbox
                      label={
                        <Text size={PX14}>
                          <Value name="apps.web.src.playlists.components.addlessontolistmodal.deselecteer_alles">
                            (De)selecteer alles
                          </Value>
                        </Text>
                      }
                      checked={deselectBoxState}
                      onCheckedChange={(value) => {
                        if (deselectBoxState) {
                          setGrades([])
                          setDeselectBoxState(false)
                        } else {
                          userGroups &&
                            setGrades([
                              ...userGroups.map(
                                (userGroup: any) => userGroup.id
                              ),
                            ])
                        }
                      }}
                    />
                  </Group>
                  {userGroups?.map((userGroup: any) => {
                    const isCurrentGroup = activeGroup?.id === userGroup.id
                    return (
                      <Group
                        key={userGroup.id}
                        gap="16px"
                        align="center"
                        justify="space-between"
                      >
                        <Group gap="16px" align="center">
                          <Checkbox
                            label={
                              <Text
                                bold={currentGrades.includes(userGroup.id)}
                                size={PX14}
                              >
                                {userGroup.displayTitle}
                              </Text>
                            }
                            value={userGroup.id}
                            checked={currentGrades.includes(userGroup.id)}
                            onCheckedChange={() =>
                              setGrades(toggleArray(userGroup.id))
                            }
                          />
                        </Group>
                        <Group align="center" justify="space-between">
                          {isCurrentGroup && (
                            <Text size={PX14} color={ADD_PLAYLIST_BLUE_COLOR}>
                              <Value name="apps.web.src.playlists.components.addlessontolistmodal.huidige_groep">
                                Huidige groep
                              </Value>
                            </Text>
                          )}
                        </Group>
                      </Group>
                    )
                  })}
                  <Separator />
                </Stack>
              </Stack>
            </DrawerDescription>
            <DrawerFooter>
              <DrawerClose asChild>
                <RoundedButton variant="filled">
                  <Value name="apps.web.src.studio.components.studiothemegroup.filter_resultaten">
                    Filter resultaten
                  </Value>
                </RoundedButton>
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </DrawerPortal>
      )}
    </Drawer>
  )
}

function AddLessonToListModalInner({
  trigger,
  isOpen,
  setOpen,
  ...props
}: {
  trigger?: ReactNode
  isOpen: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  lessons: AddToPlaylistLesson[]
}) {
  const [
    isSubmittableToKidsListWarningOpen,
    setSubmittableToKidsListWarningOpen,
  ] = useState(false)
  const activeGroup = useCurrentGroup()
  const { data: userGroups } = useSuspenseQuery(
    trpc.users.getUserGroups.queryOptions()
  )
  const [deselectBoxState, setDeselectBoxState] = useState<CheckedState>(false)

  const [currentGrades, setGrades] = useState<number[]>(
    activeGroup ? [activeGroup.id] : []
  )
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [value, setValue] = useState<string[]>([])

  useEffect(() => {
    currentGrades.length === 0
      ? setDeselectBoxState(false)
      : currentGrades.length === userGroups?.length
        ? setDeselectBoxState(true)
        : setDeselectBoxState("indeterminate")
  }, [currentGrades.length, userGroups?.length])

  return (
    <>
      {isSubmittableToKidsListWarningOpen && (
        <AddSubmittableToPlaylistWarning
          lessons={props.lessons}
          open={isSubmittableToKidsListWarningOpen}
          onOpenChange={setSubmittableToKidsListWarningOpen}
        />
      )}

      <AddLessonToListModalContent
        {...props}
        open={isOpen}
        onOpenChange={setOpen}
        onClose={() => setOpen(false)}
        setSubmittableToKidsListWarningOpen={
          setSubmittableToKidsListWarningOpen
        }
        currentGrades={currentGrades}
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        value={value}
        setValue={setValue}
        trigger={trigger}
      />

      <DrawerContent_
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        userGroups={userGroups}
        activeGroup={activeGroup}
        currentGrades={currentGrades}
        setGrades={setGrades}
        deselectBoxState={deselectBoxState}
        setDeselectBoxState={setDeselectBoxState}
      />
    </>
  )
}

export function AddLessonToListModalUncontrolled({
  trigger,
  isOpen,
  setOpen,
  ...props
}: {
  trigger?: ReactNode
  isOpen: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  lessons: AddToPlaylistLesson[]
}) {
  if (!isOpen) {
    // When closed, render minimal Dialog with just the trigger
    return (
      <Dialog.Root open={isOpen} onOpenChange={setOpen}>
        {trigger ? <Dialog.Trigger asChild>{trigger}</Dialog.Trigger> : null}
      </Dialog.Root>
    )
  }

  // When open, render full modal with data loading
  return (
    <Suspense
      fallback={
        <Dialog.Root open={isOpen} onOpenChange={setOpen}>
          {trigger ? <Dialog.Trigger asChild>{trigger}</Dialog.Trigger> : null}
        </Dialog.Root>
      }
    >
      <AddLessonToListModalInner
        {...props}
        trigger={trigger}
        isOpen={isOpen}
        setOpen={setOpen}
      />
    </Suspense>
  )
}

function AddLessonToListModal(props: {
  lessons: AddToPlaylistLesson[]
  trigger: ReactNode
}) {
  const [isOpen, setOpen] = useState(false)
  return (
    <AddLessonToListModalUncontrolled
      key={isOpen.toString()}
      {...props}
      isOpen={isOpen}
      setOpen={setOpen}
      trigger={props.trigger}
    />
  )
}

export default AddLessonToListModal
